import React, { useContext, useState, useEffect } from "react";
import dayjs from "dayjs";
import LazyLoad from "react-lazyload";

import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import SearchIcon from "@mui/icons-material/Search";

import { DatabaseContext, UserContext } from "../../context";
import UserAvatar from "../management/UserAvatar";
import firebase from "../../firebase";
import NewGroupDialog from "./NewGroupDialog";
import { avatarSrc, uniq } from "../../utility";

export default function NewConversationDialog(props) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const { users, folders } = useContext(DatabaseContext);
    const [availableUsers, setAvailableUsers] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [usernameFilter, setUsernameFilter] = useState("");
    const [groupDialogOpen, setGroupDialogOpen] = useState(false);
    const { currentUserData } = useContext(UserContext);

    useEffect(() => {
        if (currentUserData) {
            var userKeys = [];
            folders
                .filter((folder) => folder.hub)
                .forEach((hub) => {
                    if (hub.users) {
                        if (hub.users.includes(currentUserData.key)) {
                            hub.managers
                                .filter(
                                    (user) => user !== currentUserData.key,
                                )
                                .forEach((user) => {
                                    userKeys.push(user);
                                });

                            hub.users
                                .filter(
                                    (user) => user !== currentUserData.key,
                                )
                                .forEach((user) => {
                                    userKeys.push(user);
                                });
                        }
                    }

                    if (hub.managers) {
                        if (hub.managers.includes(currentUserData.key)) {
                            hub.managers
                                .filter(
                                    (user) => user !== currentUserData.key,
                                )
                                .forEach((user) => {
                                    userKeys.push(user);
                                });

                            hub.users
                                .filter(
                                    (user) => user !== currentUserData.key,
                                )
                                .forEach((user) => {
                                    userKeys.push(user);
                                });
                        }
                    }
                });

            var usersToAdd = [];
            uniq(userKeys).forEach((userKey) => {
                let matchingUser = users.find((user) => user.key === userKey);

                if (matchingUser) {
                    usersToAdd.push(matchingUser);
                }
            });

            setAvailableUsers(usersToAdd);
        }
    }, [currentUserData, users, folders]);

    const handleUserSelect = (user) => () => {
        let selectedArray = [...selectedUsers];
        selectedArray.push(user);
        setSelectedUsers(selectedArray);
        setUsernameFilter("");
    };

    const handleDeleteChip = (user) => () => {
        let selectedArray = selectedUsers.filter(
            (selectedUser) => selectedUser.key !== user.key,
        );

        setSelectedUsers(selectedArray);
    };

    const handleClose = (didAdd) => {
        setUsernameFilter("");
        setSelectedUsers([]);
        props.onClose(didAdd);
    };

    const handleChangeFilterText = (event) => {
        setUsernameFilter(event.target.value);
    };

    function handleCloseGroupDialog() {
        setGroupDialogOpen(false);
    }

    function handleAddClick() {
        if (selectedUsers.length === 1) {
            const ref = firebase.database().ref().child("conversations");
            let selectedUserId = selectedUsers[0].key;

            let conversation = {
                participants: {
                    [currentUserData.key]: true,
                    [selectedUserId]: true,
                },
                lastMessage: dayjs().toString(),
                deleted: false,
            };
            ref.push(conversation);
            handleClose(true);
        } else if (selectedUsers.length > 1) {
            setGroupDialogOpen(true);
        }
    }

    function createGroup(group, downloadURL) {
        setGroupDialogOpen(false);
        const ref = firebase.database().ref().child("conversations");

        let participants = { [currentUserData.key]: true };

        selectedUsers.forEach((user) => {
            let key = user.key;
            participants[key] = true;
        });

        ref.child(group.key).set({
            title: group.title,
            participants: participants,
            imageURL: downloadURL || "",
            lastMessage: dayjs().toString(),
            deleted: false,
        });

        handleClose(true);
    }

    const filteredUsers = availableUsers
        .filter(
            (user) =>
                currentUserData &&
                user.key !== currentUserData.key &&
                selectedUsers.filter(
                    (selectedUser) => selectedUser.key === user.key,
                ).length === 0,
        )
        .filter((user) => user.fullName.includes(usernameFilter));

    return (
        <Dialog
            fullScreen={fullScreen}
            fullWidth
            maxWidth="sm"
            onClose={() => handleClose(false)}
            open={props.open}
            aria-labelledby="new-conversation-title"
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: fullScreen ? 0 : 3,
                        overflow: "hidden",
                        backgroundColor: "background.paper",
                    },
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    px: { xs: 2, sm: 3 },
                    py: 2.5,
                }}
            >
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                        borderRadius: 2,
                        color: "primary.main",
                        backgroundColor: "rgba(0, 149, 61, 0.09)",
                    }}
                >
                    <PersonAddAltOutlinedIcon />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        id="new-conversation-title"
                        variant="h5"
                        component="h2"
                        sx={{ lineHeight: 1.1 }}
                    >
                        New conversation
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                    >
                        Choose one person for a direct message or several for a
                        group.
                    </Typography>
                </Box>
                <IconButton
                    aria-label="Close new conversation"
                    onClick={() => handleClose(false)}
                    sx={{ mt: -0.75, mr: -1 }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent
                dividers
                sx={{
                    p: { xs: 2, sm: 2.5 },
                    backgroundColor: "background.default",
                }}
            >
                {selectedUsers.length > 0 && (
                    <Box
                        sx={{
                            mb: 2,
                            p: 1.5,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 2.5,
                            backgroundColor: "background.paper",
                        }}
                    >
                        <Typography
                            variant="overline"
                            color="text.secondary"
                            sx={{ display: "block", mb: 0.75, lineHeight: 1.4 }}
                        >
                            Selected · {selectedUsers.length}
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                            {selectedUsers.map((user) => (
                                <Chip
                                    avatar={
                                        <Avatar
                                            alt={user.fullName}
                                            src={avatarSrc(user)}
                                        />
                                    }
                                    key={user.key}
                                    label={user.fullName}
                                    onDelete={handleDeleteChip(user)}
                                    variant="outlined"
                                    sx={{
                                        borderColor: "rgba(0, 149, 61, 0.22)",
                                        backgroundColor:
                                            "rgba(0, 149, 61, 0.045)",
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>
                )}

                <TextField
                    id="conversation-user-search"
                    label="Search people"
                    fullWidth
                    value={usernameFilter}
                    onChange={handleChangeFilterText}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        mb: 1.5,
                        "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            backgroundColor: "background.paper",
                        },
                    }}
                />

                <Box
                    sx={{
                        overflow: "hidden",
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2.5,
                        backgroundColor: "background.paper",
                    }}
                >
                    <List disablePadding sx={{ maxHeight: { xs: "none", sm: 380 } }}>
                        {filteredUsers.map((user, index) => (
                            <ListItemButton
                                key={user.key}
                                onClick={handleUserSelect(user)}
                                sx={{
                                    gap: 1.5,
                                    px: 1.75,
                                    py: 1.25,
                                    borderBottom:
                                        index < filteredUsers.length - 1
                                            ? "1px solid"
                                            : "none",
                                    borderColor: "divider",
                                }}
                            >
                                <LazyLoad overflow>
                                    <UserAvatar
                                        userOrHub={user}
                                        path="users"
                                        variant="circular"
                                    />
                                </LazyLoad>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="subtitle2" noWrap>
                                        {user.fullName}
                                    </Typography>
                                </Box>
                                <Typography
                                    variant="caption"
                                    sx={{ color: "primary.main", fontWeight: 800 }}
                                >
                                    Add
                                </Typography>
                            </ListItemButton>
                        ))}

                        {filteredUsers.length === 0 && (
                            <Box sx={{ px: 3, py: 5, textAlign: "center" }}>
                                <Typography variant="subtitle2">
                                    No people found
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mt: 0.35 }}
                                >
                                    Try another name or check your hub membership.
                                </Typography>
                            </Box>
                        )}
                    </List>
                </Box>
            </DialogContent>

            <DialogActions
                sx={{
                    px: { xs: 2, sm: 3 },
                    py: 1.5,
                    gap: 1,
                    pb: { xs: "max(12px, env(safe-area-inset-bottom))", sm: 1.5 },
                }}
            >
                <Button color="inherit" onClick={() => handleClose(false)}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disableElevation
                    disabled={selectedUsers.length === 0}
                    onClick={handleAddClick}
                    endIcon={<ArrowForwardIcon />}
                >
                    {selectedUsers.length > 1 ? "Continue" : "Start conversation"}
                </Button>
            </DialogActions>

            <NewGroupDialog
                open={groupDialogOpen}
                onClose={handleCloseGroupDialog}
                createGroup={createGroup}
            />
        </Dialog>
    );
}
